import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import api from '../../../domain/api';
import socket from '../../../socket';
import defaultPic from '../../../assets/upload.jpeg';
import moment from 'moment';
import { URI } from '../../../domain/constant';

// Modern Chat System with Tabs and Improved UI
export function ModernChatSystem({ currentUserId }) {
  const history = useHistory();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('chats'); // 'chats' or 'users'
  const [conversations, setConversations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isAppInFocus, setIsAppInFocus] = useState(true); // Track window focus

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Track window focus - for push notifications when user is not actively using app
  useEffect(() => {
    const handleFocus = () => {
      console.log('👁️ App focused - push notifications disabled');
      setIsAppInFocus(true);
    };

    const handleBlur = () => {
      console.log('👁️ App blurred - push notifications enabled');
      setIsAppInFocus(false);
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Scroll to bottom function
  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Diagnostic function for troubleshooting notifications
  const diagnoseNotifications = () => {
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🔍 NOTIFICATION DIAGNOSTICS');
    console.log('═══════════════════════════════════════════════════════════');

    // 1. Browser API support
    console.log('1️⃣ Browser Support:');
    console.log(`   - 'Notification' in window: ${'Notification' in window}`);
    console.log(`   - 'serviceWorker' in navigator: ${'serviceWorker' in navigator}`);
    console.log(`   - 'PushManager' in window: ${'PushManager' in window}`);

    // 2. Current permission state
    if ('Notification' in window) {
      console.log('\n2️⃣ Notification Permission:');
      console.log(`   - Current state: "${Notification.permission}"`);
      console.log(`   - Possible values: "granted" | "denied" | "default"`);

      if (Notification.permission === 'granted') {
        console.log(`   ✅ GOOD: Permission is GRANTED`);
      } else if (Notification.permission === 'denied') {
        console.log(`   ❌ PROBLEM: Permission is DENIED`);
        console.log(`   📍 FIX: Go to browser settings > Notifications > Allow this site`);
      } else if (Notification.permission === 'default') {
        console.log(`   ❓ INFO: Permission not yet requested`);
        console.log(`   📍 FIX: Reload page and accept permission prompt`);
      }
    }

    // 3. Service Worker status
    if ('serviceWorker' in navigator) {
      console.log('\n3️⃣ Service Worker:');
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        console.log(`   - Registered: ${registrations.length > 0}`);
        registrations.forEach((reg, i) => {
          console.log(`     [${i}] Scope: ${reg.scope}`);
          console.log(`         Active: ${reg.active ? '✅' : '❌'}`);
        });
      });
    }

    // 4. Audio file check
    console.log('\n4️⃣ Audio File (/sound.mp3):');
    fetch('/sound.mp3', { method: 'HEAD' })
      .then(res => {
        if (res.ok) {
          console.log(`   ✅ Audio file exists (Status: ${res.status})`);
        } else {
          console.log(`   ❌ Audio file not found (Status: ${res.status})`);
        }
      })
      .catch(err => console.log(`   ❌ Audio file check failed: ${err.message}`));

    // 5. Test notification
    console.log('\n5️⃣ Test Notification:');
    console.log(`   To test: Run this in console:`);
    console.log(`   new Notification('Test', { body: 'If you see this, notifications work!' })`);

    console.log('\n═══════════════════════════════════════════════════════════\n');
  };

  // Run diagnostics when component mounts (in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📱 Chat system mounted - running diagnostics');
      diagnoseNotifications();
      // Make available globally for manual testing
      window.diagnoseNotifications = diagnoseNotifications;
    }
  }, []);

  // Fetch initial data
  useEffect(() => {
    fetchConversations();
    fetchAllUsers();
  }, []);

  // Fetch conversations
  const fetchConversations = async () => {
    try {
      const res = await api.get('/chat/conversations');
      setConversations(res.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  // Fetch all users
  const fetchAllUsers = async () => {
    try {
      const res = await api.get('/users');
      const others = res.data.filter((u) => u._id !== currentUserId);
      setAllUsers(others);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Online users tracking
  useEffect(() => {
    const handleOnlineUpdate = (ids) => {
      setOnlineUserIds(ids);
    };

    socket.on('online-users', handleOnlineUpdate);

    api.get('/online-users').then((res) => {
      setOnlineUserIds(res.data);
    });

    return () => socket.off('online-users', handleOnlineUpdate);
  }, []);

  // Restore chat from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userId = params.get('userId');
    if (userId && allUsers.length > 0 && !selectedChat) {
      const user = allUsers.find(u => u._id === userId);
      if (user) {
        handleSelectChat(user);
      }
    }
  }, [allUsers, location.search]);

  // Handle chat selection
  const handleSelectChat = async (user) => {
    console.log('📱 Selecting chat with user:', user);

    // Update URL with selected chat
    history.push(`${location.pathname}?userId=${user._id}`);

    setSelectedChat(user);
    setLoading(true);

    try {
      console.log('🔍 Fetching messages for user ID:', user._id);
      const res = await api.get(`/chat/messages/${user._id}`);
      console.log('✅ Messages received:', res.data.length, 'messages');
      setMessages(res.data);

      // Mark as read
      socket.emit('mark-read', { otherUserId: user._id });

      // Clear unread count
      setConversations((prev) =>
        prev.map((conv) =>
          conv.user._id === user._id ? { ...conv, unreadCount: 0 } : conv
        )
      );

      // Scroll to bottom after messages load
      setTimeout(() => scrollToBottom(), 150);
    } catch (error) {
      console.error('❌ Error fetching messages:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Real-time message handling
  useEffect(() => {
    const handleNewMessage = (message) => {
      // Normalize IDs to strings for comparison
      const fromId = String(message.from._id || message.from);
      const toId = String(message.to?._id || message.to);
      const currentId = String(currentUserId);
      const selectedId = String(selectedChat?._id);

      // Determine the other user's ID
      const otherUserId = fromId === currentId ? toId : fromId;

      console.log('📨 Received message:', {
        fromId,
        toId,
        otherUserId,
        selectedId,
        matches: otherUserId === selectedId,
      });

      // ✅ SHOW NOTIFICATION (Browser notification for incoming messages)
      if (fromId !== currentId && message.content) {
        console.log('📢 [NOTIFICATION DEBUG] Incoming message detected from different user');

        // Play audio notification
        const audio = new Audio('/sound.mp3');
        audio.play().catch((err) => {
          console.warn('🔇 Audio play failed:', err.message);
        });

        // Show browser notification
        console.log('🔔 [NOTIFICATION DEBUG] Current permission status:', Notification.permission);

        if (Notification.permission === 'granted') {
          try {
            const senderName = (message.from && typeof message.from === 'object')
              ? message.from.name
              : allUsers.find(u => String(u._id) === fromId)?.name || 'Someone';

            const notification = new Notification('💬 New Message', {
              body: `${senderName}: ${message.content}`,
              icon: '/chat-icon.png',
              silent: true,
              tag: 'chat-notification',
            });

            notification.onclick = () => {
              window.focus();
              handleSelectChat(allUsers.find(u => String(u._id) === fromId));
            };

            console.log('✅ Notification shown for:', senderName);
          } catch (error) {
            console.warn('⚠️ Notification failed:', error);
          }
        } else {
          console.warn('❌ [NOTIFICATION DEBUG] Notification permission NOT granted!');
          console.warn('   Current permission:', Notification.permission);
          console.warn('   To fix: User must grant notification permission in browser settings');
        }
      }

      // Add to messages if current chat and IDs match
      if (selectedChat && otherUserId === selectedId) {
        console.log('✅ Message added to current chat');
        setMessages((prev) => {
          const updated = [...prev, message];
          // Scroll to bottom after adding message
          setTimeout(() => scrollToBottom(), 50);
          return updated;
        });
      }

      // Update conversations
      updateConversationList(message);
    };

    const handleMessageSent = (message) => {
      console.log('📤 Message sent confirmation received');
      if (selectedChat) {
        setMessages((prev) => {
          const updated = [...prev, message];
          // Scroll to bottom after adding message
          setTimeout(() => scrollToBottom(), 50);
          return updated;
        });
      }
      updateConversationList(message);
    };

    const handleTyping = ({ userId }) => {
      const typingId = String(userId);
      const selectedId = String(selectedChat?._id);

      if (selectedChat && typingId === selectedId) {
        setTypingUsers((prev) => ({ ...prev, [userId]: true }));
        setTimeout(() => {
          setTypingUsers((prev) => ({ ...prev, [userId]: false }));
        }, 3000);
      }
    };

    socket.on('receive-message', handleNewMessage);
    socket.on('message-sent', handleMessageSent);
    socket.on('user-typing', handleTyping);

    return () => {
      socket.off('receive-message', handleNewMessage);
      socket.off('message-sent', handleMessageSent);
      socket.off('user-typing', handleTyping);
    };
  }, [selectedChat, currentUserId, scrollToBottom, allUsers, handleSelectChat]);

  // Update conversation list
  const updateConversationList = (message) => {
    const otherUserId =
      message.from._id === currentUserId || message.from === currentUserId
        ? message.to._id || message.to
        : message.from._id || message.from;

    const isSentByMe =
      message.from._id === currentUserId || message.from === currentUserId;

    setConversations((prev) => {
      const existingIndex = prev.findIndex(
        (conv) => conv.user._id === otherUserId
      );

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          lastMessage: {
            content: message.content,
            timestamp: message.timestamp || message.createdAt,
            from: message.from._id || message.from,
            type: message.type,
          },
          unreadCount: isSentByMe
            ? updated[existingIndex].unreadCount
            : (updated[existingIndex].unreadCount || 0) + 1,
        };

        const [movedConv] = updated.splice(existingIndex, 1);
        return [movedConv, ...updated];
      } else {
        // New conversation
        const userInfo = allUsers.find((u) => u._id === otherUserId);
        if (userInfo) {
          return [
            {
              user: userInfo,
              lastMessage: {
                content: message.content,
                timestamp: message.timestamp || message.createdAt,
                from: message.from._id || message.from,
                type: message.type,
              },
              unreadCount: isSentByMe ? 0 : 1,
            },
            ...prev,
          ];
        }
      }
      return prev;
    });
  };

  // Send message
  const handleSendMessage = async () => {
    if (!messageInput.trim() && selectedFiles.length === 0) return;
    if (!selectedChat) return;

    // Handle multiple file uploads
    if (selectedFiles.length > 0) {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);

        try {
          const res = await api.post('/chat/upload', formData);
          const fileData = res.data;

          // Set appropriate content message based on file type
          const isImage = fileData.mimeType.startsWith('image/');
          const defaultContent = isImage ? '📸 Sent an image' : '📎 Sent a file';

          const messageData = {
            to: selectedChat._id,
            content: defaultContent,
            type: isImage ? 'image' : 'file',
            fileUrl: fileData.fileUrl,
            fileName: fileData.fileName,
            fileSize: fileData.fileSize,
            mimeType: fileData.mimeType,
            senderAppInFocus: isAppInFocus, // Track if sender's app is in focus
          };

          socket.emit('send-message', messageData);
        } catch (error) {
          console.error('File upload failed:', error);
        }
      }

      setSelectedFiles([]);
    }

    // Handle text message
    if (messageInput.trim()) {
      const messageData = {
        to: selectedChat._id,
        content: messageInput.trim(),
        type: 'text',
        senderAppInFocus: isAppInFocus, // Track if sender's app is in focus
      };

      socket.emit('send-message', messageData);
    }

    setMessageInput('');

    // Scroll to bottom after sending message
    setTimeout(() => scrollToBottom(), 100);
  };

  // Typing indicator
  const handleTyping = () => {
    if (selectedChat) {
      socket.emit('typing', { to: selectedChat._id });
    }
  };

  // Drag & Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set isDragging if the drag contains files
    if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Set effect to copy for files
    if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set isDragging to false if truly leaving the drop zone
    // Check if the related target is outside the current element
    if (e.relatedTarget === null || !e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    // Handle both files from dataTransfer.files and items (for better browser support)
    let files = [];

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      files = Array.from(e.dataTransfer.files);
    } else if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      // Fallback for some browsers - convert DataTransferItemList to array
      files = Array.from(e.dataTransfer.items)
        .filter(item => item.kind === 'file')
        .map(item => item.getAsFile())
        .filter(file => file !== null);
    }

    if (files.length > 0) {
      setSelectedFiles(files);
    }
  };

  // Format time
  const formatTime = (timestamp) => {
    return moment(timestamp).format('HH:mm');
  };

  const formatRelativeTime = (timestamp) => {
    const now = moment();
    const msgTime = moment(timestamp);
    const diffMinutes = now.diff(msgTime, 'minutes');
    const diffHours = now.diff(msgTime, 'hours');
    const diffDays = now.diff(msgTime, 'days');

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return msgTime.format('DD MMM');
  };

  // Filter logic
  const filteredConversations = conversations.filter((conv) =>
    conv.user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUsers = allUsers.filter((user) =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUnreadCount = conversations.reduce(
    (sum, conv) => sum + (conv.unreadCount || 0),
    0
  );

  const isUserOnline = (userId) => onlineUserIds.includes(userId);

  // Get appropriate icon for file type
  const getFileIcon = (mimeType) => {
    if (mimeType.includes('pdf')) return 'fa-file-pdf-o';
    if (mimeType.includes('word') || mimeType.includes('doc')) return 'fa-file-word-o';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'fa-file-excel-o';
    if (mimeType.includes('video')) return 'fa-file-video-o';
    if (mimeType.includes('audio')) return 'fa-file-audio-o';
    if (mimeType.includes('zip') || mimeType.includes('rar')) return 'fa-file-archive-o';
    if (mimeType.includes('text')) return 'fa-file-text-o';
    return 'fa-file-o';
  };

  // Get message status indicator (WhatsApp-style)
  const getMessageStatus = (message, isMine) => {
    if (!isMine) return null; // Only show status for sent messages

    if (message.seenAt) {
      return {
        status: 'read',
        tooltip: `Read at ${formatTime(message.seenAt)}`,
        className: 'msg-status-read',
        icon: '✓✓',
      };
    } else if (message.deliveredAt) {
      return {
        status: 'delivered',
        tooltip: `Delivered at ${formatTime(message.deliveredAt)}`,
        className: 'msg-status-delivered',
        icon: '✓✓',
      };
    } else {
      return {
        status: 'sent',
        tooltip: `Sent at ${formatTime(message.timestamp || message.createdAt)}`,
        className: 'msg-status-sent',
        icon: '✓',
      };
    }
  };

  // Message Status Indicator Component
  const MessageStatusIndicator = ({ message, isMine }) => {
    const status = getMessageStatus(message, isMine);
    if (!status) return null;

    return (
      <span
        className={`message-status ${status.className}`}
        title={status.tooltip}
      >
        {status.icon}
      </span>
    );
  };

  return (
    <>
      <div className='modern-chat-container'>
        {/* Sidebar */}
        <div className='chat-sidebar'>
          {/* Sidebar Header */}
          <div className='sidebar-header'>
            <h2 className='sidebar-title'>Messages</h2>
          </div>

          {/* Tab Navigation */}
          <div className='tab-navigation'>
            <button
              className={`tab-btn ${activeTab === 'chats' ? 'active' : ''}`}
              onClick={() => setActiveTab('chats')}
            >
              <i className='fa fa-comments'></i>
              Chats
              {totalUnreadCount > 0 && (
                <span className='tab-badge'>{totalUnreadCount}</span>
              )}
            </button>
            <button
              className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              <i className='fa fa-users'></i>
              All Users
              <span className='tab-count'>({allUsers.length})</span>
            </button>
          </div>

          {/* Search Bar */}
          <div className='search-container'>
            <i className='fa fa-search search-icon'></i>
            <input
              type='text'
              placeholder={
                activeTab === 'chats'
                  ? 'Search conversations...'
                  : 'Search users...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='search-input'
            />
          </div>

          {/* List */}
          <div className='chat-list'>
            {activeTab === 'chats' ? (
              // Conversations Tab
              filteredConversations.length === 0 ? (
                <div className='empty-state'>
                  <i className='fa fa-comments-o'></i>
                  <p>No conversations yet</p>
                  <small>Go to "All Users" to start chatting</small>
                </div>
              ) : (
                filteredConversations.map((conv) => (
                  <div
                    key={conv.user._id}
                    className={`list-item ${
                      selectedChat?._id === conv.user._id ? 'active' : ''
                    } ${conv.unreadCount > 0 ? 'has-unread' : ''}`}
                    onClick={() => handleSelectChat(conv.user)}
                  >
                    <div className='item-avatar-wrapper'>
                      <img
                        src={conv.user.avatar || defaultPic}
                        alt={conv.user.name}
                        className='item-avatar'
                      />
                      {isUserOnline(conv.user._id) && (
                        <div className='online-dot'></div>
                      )}
                    </div>
                    <div className='item-content'>
                      <div className='item-header'>
                        <h4
                          className={`item-name ${
                            conv.unreadCount > 0 ? 'unread' : ''
                          }`}
                        >
                          {conv.user.name}
                        </h4>
                        <span className='item-time'>
                          {conv.lastMessage &&
                            formatRelativeTime(conv.lastMessage.timestamp)}
                        </span>
                      </div>
                      <div className='item-preview'>
                        <p
                          className={`item-message ${
                            conv.unreadCount > 0 ? 'unread' : ''
                          }`}
                        >
                          {conv.lastMessage?.from === currentUserId && 'You: '}
                          {conv.lastMessage?.content || 'No messages yet'}
                        </p>
                        {conv.unreadCount > 0 && (
                          <span className='unread-badge'>
                            {conv.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )
            ) : (
              // All Users Tab
              filteredUsers.length === 0 ? (
                <div className='empty-state'>
                  <i className='fa fa-user-slash'></i>
                  <p>No users found</p>
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <div
                    key={user._id}
                    className={`list-item ${
                      selectedChat?._id === user._id ? 'active' : ''
                    }`}
                    onClick={() => handleSelectChat(user)}
                  >
                    <div className='item-avatar-wrapper'>
                      <img
                        src={user.avatar || defaultPic}
                        alt={user.name}
                        className='item-avatar'
                      />
                      {isUserOnline(user._id) && (
                        <div className='online-dot'></div>
                      )}
                    </div>
                    <div className='item-content'>
                      <h4 className='item-name'>{user.name}</h4>
                      <p className='item-email'>{user.email}</p>
                    </div>
                    {isUserOnline(user._id) && (
                      <span className='online-label'>Online</span>
                    )}
                  </div>
                ))
              )
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className='chat-main'>
          {selectedChat ? (
            <>
              {/* Chat Header */}
              <div className='chat-header'>
                <div className='chat-header-user'>
                  <img
                    src={selectedChat.avatar || defaultPic}
                    alt={selectedChat.name}
                    className='chat-header-avatar'
                  />
                  <div>
                    <h3 className='chat-header-name'>{selectedChat.name}</h3>
                    <p className='chat-header-status'>
                      {typingUsers[selectedChat._id] ? (
                        <span className='typing-indicator'>typing...</span>
                      ) : isUserOnline(selectedChat._id) ? (
                        <span className='online-text'>
                          <i className='fa fa-circle'></i> Online
                        </span>
                      ) : (
                        'Offline'
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div
                className='messages-area'
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {(() => {
                  console.log('💬 Render state - Loading:', loading, '| Messages count:', messages.length, '| Selected chat:', selectedChat?.name);
                  return null;
                })()}
                {loading ? (
                  <div className='loading-messages'>
                    <i className='fa fa-spinner fa-spin'></i>
                    Loading messages...
                  </div>
                ) : messages.length === 0 ? (
                  <div className='no-messages'>
                    <i className='fa fa-comments-o'></i>
                    <p>No messages yet</p>
                    <small>Start the conversation!</small>
                  </div>
                ) : (
                  messages.map((msg) => {
                    // Normalize IDs to strings for comparison
                    const fromId = typeof msg.from === 'object' && msg.from?._id
                      ? msg.from._id
                      : msg.from;
                    const isMine = String(fromId) === String(currentUserId);

                    return (
                      <div
                        key={msg._id}
                        className={`message ${isMine ? 'mine' : 'theirs'}`}
                      >
                        {msg.type === 'image' ? (
                          <img
                            src={`${URI}${msg.fileUrl}`}
                            alt='Shared'
                            className='message-image'
                            onClick={() => window.open(`${URI}${msg.fileUrl}`, '_blank')}
                          />
                        ) : msg.type === 'file' ? (
                          <div className='message-file'>
                            <i className='fa fa-file'></i>
                            <span>{msg.fileName}</span>
                            <a
                              href={`${URI}${msg.fileUrl}`}
                              download
                              className='download-btn'
                            >
                              <i className='fa fa-download'></i>
                            </a>
                          </div>
                        ) : (
                          <p className='message-text'>{msg.content}</p>
                        )}
                        <span className='message-time'>
                          {formatTime(msg.timestamp || msg.createdAt)}
                        </span>
                      </div>
                    );
                  })
                )}

                {/* Drag overlay */}
                {isDragging && (
                  <div className='drag-overlay'>
                    <div className='drag-content'>
                      <i className='fa fa-upload fa-3x'></i>
                      <p>Drop files here to send</p>
                    </div>
                  </div>
                )}

                {/* Invisible element to scroll to */}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className='chat-input-container'>
                {selectedFiles.length > 0 && (
                  <div className='files-preview-container'>
                    <div className='files-preview-header'>
                      <span>{selectedFiles.length} file(s) selected</span>
                      <button
                        className='clear-all-btn'
                        onClick={() => setSelectedFiles([])}
                      >
                        Clear all
                      </button>
                    </div>
                    <div className='files-preview-list'>
                      {selectedFiles.map((file, index) => (
                        <div key={index} className='file-preview-item'>
                          {file.type.startsWith('image/') ? (
                            <img
                              src={URL.createObjectURL(file)}
                              alt='Preview'
                              className='file-preview-thumb'
                            />
                          ) : (
                            <div className='file-icon-wrapper'>
                              <i className={`fa ${getFileIcon(file.type)}`}></i>
                            </div>
                          )}
                          <div className='file-preview-name-small'>
                            {file.name.length > 15 ? file.name.substring(0, 15) + '...' : file.name}
                          </div>
                          <button
                            className='file-preview-remove-small'
                            onClick={() => {
                              setSelectedFiles(prev => prev.filter((_, i) => i !== index));
                            }}
                          >
                            <i className='fa fa-times'></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className='chat-input-wrapper'>
                  <button
                    className='input-btn'
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <i className='fa fa-paperclip'></i>
                  </button>
                  <input
                    type='file'
                    ref={fileInputRef}
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                  />
                  <input
                    type='text'
                    value={messageInput}
                    onChange={(e) => {
                      setMessageInput(e.target.value);
                      handleTyping();
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder='Type a message...'
                    className='message-input'
                  />
                  <button
                    className='send-btn'
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim() && selectedFiles.length === 0}
                  >
                    <i className='fa fa-paper-plane'></i>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className='no-chat-selected'>
              <i className='fa fa-comments-o'></i>
              <h3>Select a conversation</h3>
              <p>Choose from your recent chats or start a new one</p>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .modern-chat-container {
          display: flex;
          height: calc(100vh - 140px);
          background: #f5f7fa;
          overflow: hidden;
        }

        /* === SIDEBAR === */
        .chat-sidebar {
          width: 380px;
          background: white;
          border-right: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
        }

        .sidebar-header {
          padding: 20px;
          border-bottom: 1px solid #f3f4f6;
        }

        .sidebar-title {
          margin: 0;
          font-size: 24px;
          font-weight: 700;
          color: #111827;
        }

        .tab-navigation {
          display: flex;
          padding: 12px 16px;
          gap: 8px;
          border-bottom: 1px solid #f3f4f6;
          background: #fafbfc;
        }

        .tab-btn {
          flex: 1;
          padding: 10px 16px;
          border: none;
          background: white;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          position: relative;
        }

        .tab-btn:hover {
          background: #f9fafb;
          color: #374151;
        }

        .tab-btn.active {
          background: #dbeafe;
          color: #1e40af;
        }

        .tab-badge {
          background: #ef4444;
          color: white;
          font-size: 11px;
          padding: 2px 6px;
          border-radius: 10px;
          font-weight: 700;
        }

        .tab-count {
          color: inherit;
          opacity: 0.7;
          font-size: 12px;
        }

        .search-container {
          padding: 16px;
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 28px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          font-size: 14px;
        }

        .search-input {
          width: 100%;
          padding: 10px 12px 10px 36px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.2s;
        }

        .search-input:focus {
          outline: none;
          border-color: #93c5fd;
          box-shadow: 0 0 0 3px rgba(147, 197, 253, 0.15);
        }

        .chat-list {
          flex: 1;
          overflow-y: auto;
        }

        .list-item {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          cursor: pointer;
          transition: all 0.2s;
          border-bottom: 1px solid #f9fafb;
        }

        .list-item:hover {
          background: #f9fafb;
        }

        .list-item.active {
          background: #eff6ff;
          border-left: 3px solid #3b82f6;
        }

        .list-item.has-unread {
          background: #fefefe;
        }

        .item-avatar-wrapper {
          position: relative;
          margin-right: 12px;
        }

        .item-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          object-fit: cover;
        }

        .online-dot {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 12px;
          height: 12px;
          background: #10b981;
          border: 2px solid white;
          border-radius: 50%;
        }

        .item-content {
          flex: 1;
          min-width: 0;
        }

        .item-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
        }

        .item-name {
          font-size: 15px;
          font-weight: 600;
          color: #111827;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .item-name.unread {
          font-weight: 700;
        }

        .item-time {
          font-size: 12px;
          color: #9ca3af;
        }

        .item-preview {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .item-message {
          font-size: 13px;
          color: #6b7280;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .item-message.unread {
          font-weight: 600;
          color: #374151;
        }

        .item-email {
          font-size: 13px;
          color: #9ca3af;
          margin: 0;
        }

        .unread-badge {
          background: #60a5fa;
          color: white;
          font-size: 11px;
          font-weight: 700;
          padding: 2px 7px;
          border-radius: 10px;
          min-width: 20px;
          text-align: center;
        }

        .online-label {
          font-size: 12px;
          color: #10b981;
          font-weight: 600;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
          color: #9ca3af;
        }

        .empty-state i {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .empty-state p {
          font-size: 16px;
          font-weight: 600;
          color: #6b7280;
          margin: 0 0 8px;
        }

        .empty-state small {
          font-size: 13px;
          color: #9ca3af;
        }

        /* === CHAT AREA === */
        .chat-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          background: white;
          overflow: hidden;
          min-height: 0;
        }

        .chat-header {
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
          background: white;
        }

        .chat-header-user {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .chat-header-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          object-fit: cover;
        }

        .chat-header-name {
          margin: 0 0 4px;
          font-size: 18px;
          font-weight: 600;
          color: #111827;
        }

        .chat-header-status {
          margin: 0;
          font-size: 13px;
          color: #6b7280;
        }

        .online-text {
          color: #10b981;
          font-weight: 600;
        }

        .online-text i {
          font-size: 8px;
          margin-right: 4px;
        }

        .typing-indicator {
          color: #60a5fa;
          font-style: italic;
        }

        .messages-area {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          background: #f9fafb;
          position: relative;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }

        .loading-messages,
        .no-messages {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #9ca3af;
        }

        .loading-messages i,
        .no-messages i {
          font-size: 40px;
          margin-bottom: 12px;
        }

        .message {
          margin-bottom: 12px;
          max-width: 65%;
          animation: fadeIn 0.3s ease;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
        }

        .message.mine {
          margin-left: auto;
        }

        .message.theirs {
          margin-right: auto;
        }

        .message-text {
          padding: 10px 14px;
          border-radius: 16px;
          font-size: 14px;
          line-height: 1.5;
          margin: 0 0 4px;
          word-wrap: break-word;
        }

        .message.mine .message-text {
          background: #e0f2fe;
          color: #0c4a6e;
          border-bottom-right-radius: 4px;
        }

        .message.theirs .message-text {
          background: #f3f4f6;
          color: #111827;
          border-bottom-left-radius: 4px;
          border: 1px solid #e5e7eb;
        }

        .message-image {
          max-width: 300px;
          border-radius: 12px;
          cursor: pointer;
          display: block;
          transition: transform 0.2s;
        }

        .message-image:hover {
          transform: scale(1.02);
        }

        .message.mine .message-image {
          border: 3px solid #e0f2fe;
        }

        .message.theirs .message-image {
          border: 3px solid #f3f4f6;
        }

        .message-file {
          background: white;
          padding: 14px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 12px;
          max-width: 280px;
          transition: all 0.2s;
        }

        .message.mine .message-file {
          background: #e0f2fe;
          border-color: #bae6fd;
        }

        .message.theirs .message-file {
          background: white;
          border-color: #e5e7eb;
        }

        .message-file:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .message-file i.fa-file {
          font-size: 28px;
          color: #60a5fa;
        }

        .message-file span {
          flex: 1;
          font-size: 13px;
          color: #374151;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .download-btn {
          background: #60a5fa;
          color: white;
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
          flex-shrink: 0;
        }

        .download-btn:hover {
          background: #3b82f6;
          transform: scale(1.1);
        }

        .download-btn i {
          font-size: 14px;
        }

        .message-time {
          font-size: 11px;
          color: #9ca3af;
        }

        .chat-input-container {
          padding: 16px 20px;
          background: white;
          border-top: 1px solid #e5e7eb;
        }

        .file-preview {
          padding: 12px;
          background: #f3f4f6;
          border-radius: 12px;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          border: 1px solid #e5e7eb;
        }

        .file-preview i {
          font-size: 32px;
          color: #60a5fa;
        }

        .file-preview-image {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .file-preview-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
        }

        .file-preview-name {
          font-size: 14px;
          font-weight: 600;
          color: #111827;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .file-preview-size {
          font-size: 12px;
          color: #6b7280;
        }

        .file-preview-remove {
          background: #ef4444;
          color: white;
          border: none;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .file-preview-remove:hover {
          background: #dc2626;
          transform: scale(1.1);
        }

        .chat-input-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #f9fafb;
          border: 2px solid #e5e7eb;
          border-radius: 24px;
          padding: 8px 12px;
          transition: all 0.2s;
        }

        .chat-input-wrapper:focus-within {
          border-color: #93c5fd;
          background: white;
        }

        .input-btn {
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          font-size: 18px;
          padding: 4px 8px;
          transition: color 0.2s;
        }

        .input-btn:hover {
          color: #60a5fa;
        }

        .message-input {
          flex: 1;
          border: none;
          background: none;
          font-size: 14px;
          outline: none;
        }

        .send-btn {
          background: #60a5fa;
          color: white;
          border: none;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s;
        }

        .send-btn:hover:not(:disabled) {
          background: #3b82f6;
          transform: scale(1.05);
        }

        .send-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .no-chat-selected {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #9ca3af;
        }

        .no-chat-selected i {
          font-size: 64px;
          margin-bottom: 16px;
        }

        .no-chat-selected h3 {
          font-size: 20px;
          font-weight: 600;
          color: #6b7280;
          margin: 0 0 8px;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Scrollbar */
        .chat-list::-webkit-scrollbar,
        .messages-area::-webkit-scrollbar {
          width: 6px;
        }

        .chat-list::-webkit-scrollbar-track,
        .messages-area::-webkit-scrollbar-track {
          background: #f9fafb;
        }

        .chat-list::-webkit-scrollbar-thumb,
        .messages-area::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }

        /* === DRAG AND DROP === */
        .drag-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(59, 130, 246, 0.1);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          pointer-events: none;
          border: 3px dashed #3b82f6;
          border-radius: 12px;
          margin: 8px;
        }

        .drag-content {
          text-align: center;
          color: #3b82f6;
        }

        .drag-content i {
          margin-bottom: 12px;
          opacity: 0.8;
        }

        .drag-content p {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
        }

        /* === MULTIPLE FILE PREVIEW === */
        .files-preview-container {
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
          padding: 12px 16px;
          max-height: 200px;
          overflow-y: auto;
        }

        .files-preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e5e7eb;
        }

        .files-preview-header span {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
        }

        .clear-all-btn {
          background: transparent;
          border: none;
          color: #ef4444;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .clear-all-btn:hover {
          background: #fee2e2;
        }

        .files-preview-list {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .file-preview-item {
          position: relative;
          width: 80px;
          height: 100px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
        }

        .file-preview-item:hover {
          border-color: #3b82f6;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
        }

        .file-preview-thumb {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 6px;
        }

        .file-icon-wrapper {
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          border-radius: 6px;
          color: #6b7280;
        }

        .file-icon-wrapper i {
          font-size: 28px;
        }

        .file-preview-name-small {
          font-size: 11px;
          color: #6b7280;
          text-align: center;
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .file-preview-remove-small {
          position: absolute;
          top: -6px;
          right: -6px;
          width: 20px;
          height: 20px;
          background: #ef4444;
          color: white;
          border: 2px solid white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 10px;
          transition: all 0.2s;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .file-preview-remove-small:hover {
          background: #dc2626;
          transform: scale(1.1);
        }

        .file-preview-remove-small i {
          font-size: 10px;
        }

        .files-preview-container::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        .files-preview-container::-webkit-scrollbar-track {
          background: #f9fafb;
        }

        .files-preview-container::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
      `}</style>
    </>
  );
}
